// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { DashMiddleware, DashStore, DashNext } from '@makemydeal/dr-shared-store';
import { actions as offerActions } from '@makemydeal/dr-offer-redux';
import type { PaymentProduct } from '@makemydeal/dr-platform-types';

import * as configSelectors from '../selectors/config';
import * as calculationDetailsSelectors from '../selectors/calculationDetailsSelectors';
import * as dealValidationSelectors from '../selectors/dealValidationSelectors';
import * as paymentTermSelectors from '../selectors/paymentTermSelectors';
import * as vehicleProtectionSelectors from '../selectors/vehicleProtectionSelectors';
import { getIsQuoteWithoutVehicle } from '../selectors/vehicleSelectors';

import { getShopperZip } from '../selectors/offerRedux';

import {
    completeVehicleProtection,
    fetchVehicleProtectionProducts,
    fetchVehicleProtectionRates,
    repeatFetchVehicleProtectionRates,
    gapPriceAcknowledged,
    resetVppProductsMonthlyPayment,
    retryFetchVehicleProtectionRates,
    setRefreshContractValidationState,
    startVehicleProtection,
    updateGapProductPrice,
    updatePayment,
    updateVppProductMonthlyPayment,
    updateVppProductTaxed
} from '../actions/vehicleProtectionActionCreators';

import {
    ALL_PRODUCTS_SELECTED,
    ALL_PRODUCTS_UNSELECTED,
    FETCH_PRODUCTS_FAILURE,
    FETCH_PRODUCTS_RETRY,
    FETCH_PRODUCTS_SUCCESS,
    FETCH_RATES_RETRY,
    FETCH_RATES_SUCCESS,
    PRODUCT_SELECTED,
    PRODUCT_UNSELECTED,
    PRODUCT_UPDATED,
    PRODUCTS_REQUEST_OVERRIDDEN,
    RECALCULATE_PRODUCT_PAYMENTS
} from '../actionTypes/vehicleProtectionActionTypes';
import { APPLY_ORIGINAL_TEMPLATE_PRODUCTS } from '../actionTypes/dealTemplateActionTypes';
import { PAYMENT_FAILURE, PAYMENT_RECEIVED, PAYMENT_REQUESTED } from '../actionTypes/deskingActionTypes';
import { MAX_FETCH_RATES_POLLING_COUNT, RATE_REQUEST_STATUSES, RERATE_PRODUCTS_ACTION_LIST } from '../types/vehicleProtectionTypes';
import { FALLBACK_TO_TARGET_PRODUCTS_COUNT, RefreshContractValidationState } from '@makemydeal/dr-dash-types';
import { DASH_START_VEHICLE_PROTECTION } from '../actionTypes/navigationActionTypes';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { refreshContractValidationErrors } from '../actions/contractingActionCreators';

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    next(action);
    const state = store.getState();

    const isRefreshContractValidationInProgress = () => {
        const refreshState = vehicleProtectionSelectors.getRefreshContractValidationState(state);
        return (
            refreshState === RefreshContractValidationState.PENDING ||
            refreshState === RefreshContractValidationState.PENDING_AFTER_FAILURE
        );
    };

    const refreshContractValidationAndSetState = (
        nextState: RefreshContractValidationState.IDLE | RefreshContractValidationState.PENDING_AFTER_FAILURE
    ) => {
        store.dispatch(refreshContractValidationErrors());
        store.dispatch(setRefreshContractValidationState(nextState));
    };

    const isDealValid = dealValidationSelectors.isDealValid(state);
    if (!isDealValid) {
        return;
    }

    if (
        !featureToggleSelectors.areFeatureTogglesAvailable(state) ||
        !calculationDetailsSelectors.isFieCalculationEngineService(state)
    ) {
        return;
    }

    switch (action.type) {
        // NOTE: shoudn't have actions from `RERATE_PRODUCTS_ACTION_LIST` list here, covered after payment success
        case offerActions.INIT_SUCCESS:
        case offerActions.MAKE_CURRENT_OFFER:
        case PRODUCTS_REQUEST_OVERRIDDEN:
        case FETCH_PRODUCTS_RETRY:
        case offerActions.PAYMENT_GRID_FETCH_VPP_PRODUCTS: {
            if (getIsQuoteWithoutVehicle(state)) return;
            store.dispatch(fetchVehicleProtectionProducts(state, action));

            break;
        }
        case PAYMENT_REQUESTED: {
            const usePaymentProductsMonthlyPayment = featureToggleSelectors.usePaymentProductsMonthlyPayment(state);

            if (usePaymentProductsMonthlyPayment) {
                store.dispatch(resetVppProductsMonthlyPayment());
            }

            break;
        }
        case PAYMENT_RECEIVED: {
            const { originalAction } = action.meta;

            // we don't need multiple calls to get vpp products if the payment call is just for 9-box
            const shouldSkipVppFetch =
                originalAction?.meta?.paymentGrid?.isMainPayment === false || getIsQuoteWithoutVehicle(state);
            const shouldFetchVppProducts = !shouldSkipVppFetch && RERATE_PRODUCTS_ACTION_LIST.includes(originalAction?.type);

            if (shouldFetchVppProducts) {
                store.dispatch(fetchVehicleProtectionProducts(state, action));
            } else if (isRefreshContractValidationInProgress()) {
                refreshContractValidationAndSetState(RefreshContractValidationState.IDLE);
            }

            // NOTE: updating productMonthlyPayment of selected products
            const usePaymentProductsMonthlyPayment = featureToggleSelectors.usePaymentProductsMonthlyPayment(state);

            const paymentQuote = paymentTermSelectors.getCurrentPaymentTermData(state);
            const paymentProducts = paymentQuote?.products;
            const vppProducts = vehicleProtectionSelectors.getLastSuccessfulPaymentProducts(state);
            if (!paymentProducts?.length) return;

            // NOTE: PO response may contain accessories and vpp together in products[], so we have to filter it
            const nonAccessoryProducts = paymentProducts.filter((product) => product.categoryCode !== 'ACC');

            vppProducts.forEach((product, productIndex) => {
                if (!product.productCode) return;

                const taxedPaymentProduct = nonAccessoryProducts.find(
                    (paymentDetails) => paymentDetails.id === product.productCode
                );
                if (taxedPaymentProduct) {
                    store.dispatch(updateVppProductTaxed(product.productCode, taxedPaymentProduct.isTaxed ?? false));
                }

                if (usePaymentProductsMonthlyPayment) {
                    const paymentProduct = nonAccessoryProducts.find(
                        (paymentDetails: PaymentProduct, paymentProductIndex) =>
                            paymentDetails.categoryCode === product.productCategoryCode && paymentProductIndex === productIndex
                    );

                    if (product.productCode && paymentProduct?.monthlyPrice) {
                        store.dispatch(updateVppProductMonthlyPayment(product.productCode, paymentProduct.monthlyPrice));
                    }
                }
                if (product.productCategoryCode === 'GAP') {
                    if (!product.productPrice) return;

                    const gapProduct = paymentProducts.find(
                        (paymentProduct) => paymentProduct.categoryCode === 'GAP' && paymentProduct.id === product.productCode
                    );
                    if (!gapProduct) return;

                    if (gapProduct.price < product.productPrice) {
                        store.dispatch(updateGapProductPrice(product.productCode, gapProduct.price));
                    } else {
                        const isGapCappedByLender = vehicleProtectionSelectors.getIsGapCappedByLender(state);

                        if (isGapCappedByLender) store.dispatch(gapPriceAcknowledged());
                    }
                }
            });

            break;
        }
        case PAYMENT_FAILURE: {
            if (vehicleProtectionSelectors.getRefreshContractValidationState(state) === RefreshContractValidationState.PENDING) {
                refreshContractValidationAndSetState(RefreshContractValidationState.PENDING_AFTER_FAILURE);
            }
            break;
        }
        case FETCH_PRODUCTS_FAILURE: {
            if (isRefreshContractValidationInProgress()) {
                refreshContractValidationAndSetState(RefreshContractValidationState.IDLE);
            }
            break;
        }
        case FETCH_PRODUCTS_SUCCESS:
        case FETCH_RATES_RETRY: {
            const { rateToken } = action.payload;
            const bffConfig = configSelectors.getServicesBff(state);
            const scopeId = scopedStateSelectors.getCurrentScopeId(state as any);
            store.dispatch(
                fetchVehicleProtectionRates(rateToken, bffConfig, scopeId, {
                    originalAction: action
                })
            );
            if (action.type === FETCH_PRODUCTS_SUCCESS) {
                if (isRefreshContractValidationInProgress()) {
                    refreshContractValidationAndSetState(RefreshContractValidationState.IDLE);
                }
            }

            break;
        }
        case FETCH_RATES_SUCCESS: {
            const { status } = action.payload;

            if (status === RATE_REQUEST_STATUSES.PENDING) {
                const { retryNumber = 0 } = action.meta;

                const isApplyingProducts = vehicleProtectionSelectors.isApplyingTemplateProducts(state);

                if (isApplyingProducts && retryNumber === FALLBACK_TO_TARGET_PRODUCTS_COUNT) {
                    store.dispatch({
                        type: APPLY_ORIGINAL_TEMPLATE_PRODUCTS
                    });
                }

                if (retryNumber <= MAX_FETCH_RATES_POLLING_COUNT) {
                    const bffConfig = configSelectors.getServicesBff(state);
                    const scopeId = scopedStateSelectors.getCurrentScopeId(state as any);

                    store.dispatch(repeatFetchVehicleProtectionRates(action, bffConfig, scopeId));
                }
            }
            break;
        }
        case DASH_START_VEHICLE_PROTECTION: {
            const isRatingTimeout = vehicleProtectionSelectors.isVppRatingTimeout(state);

            if (isRatingTimeout) {
                store.dispatch(retryFetchVehicleProtectionRates());
            }

            store.dispatch(startVehicleProtection());

            break;
        }
        case APPLY_ORIGINAL_TEMPLATE_PRODUCTS:
        case PRODUCT_SELECTED:
        case PRODUCT_UNSELECTED:
        case ALL_PRODUCTS_UNSELECTED:
        case ALL_PRODUCTS_SELECTED:
        case PRODUCT_UPDATED:
        case RECALCULATE_PRODUCT_PAYMENTS: {
            store.dispatch(completeVehicleProtection());

            const { skipPayment } = action.meta || {};

            if (skipPayment) {
                return;
            }

            const includeTaxes = getShopperZip(state) !== '';
            const paymentProductCodes = vehicleProtectionSelectors.getVppSelectedProductCodes(state);

            store.dispatch(updatePayment(includeTaxes, paymentProductCodes));

            break;
        }
        default:
            break;
    }
};
